import { MbscCalendarEvent } from '@mobiscroll/angular';
import { Workschema } from '../types/workschema';

export interface FillEntryTimesParams {
  day: Date;
  employeeId: number | string;
  workschema: Workschema | null | undefined;
  hours: number;
  events: readonly MbscCalendarEvent[];
  fallback: { from: string; till: string };
  pickedStart?: Date | null;
}

export interface EntryTimes {
  start: Date;
  end: Date;
}


export function fillEntryTimes(params: FillEntryTimesParams): EntryTimes {
  const { day, workschema, hours, fallback } = params;
  const weekday = day.getDay() === 0 ? 7 : day.getDay();
  const schema = workschema?.detail?.find((d) => d.weekday === weekday);
  const scheduleStart = atTime(day, schema?.fromTime || fallback.from);
  const scheduleEnd = atTime(day, schema?.tillTime || fallback.till);

  const lastEnd = lastEntryEnd(params.events, params.employeeId, day);
  const start = params.pickedStart ?? lastEnd ?? scheduleStart;

  if (hours > 0) {
    return { start, end: new Date(start.getTime() + hours * 3_600_000) };
  }
  return { start, end: scheduleEnd > start ? scheduleEnd : start };
}

export function toLocalDateTimeString(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

function atTime(day: Date, time: string): Date {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    hours || 0,
    minutes || 0,
    seconds || 0
  );
}

function lastEntryEnd(
  events: readonly MbscCalendarEvent[],
  employeeId: number | string,
  day: Date
): Date | null {
  let last: Date | null = null;
  for (const event of events) {
    const resources = Array.isArray(event.resource) ? event.resource : [event.resource];
    if (!resources.some((r) => r !== undefined && String(r) === String(employeeId)))
      continue;
    const start = toDate(event.start);
    const end = toDate(event.end);
    if (!start || !end || !isSameDay(start, day)) continue;
    if (!last || end > last) last = end;
  }
  return last;
}

function toDate(value: unknown): Date | null {
  if (value === undefined || value === null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return isNaN(date.getTime()) ? null : date;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
